import PropTypes from 'prop-types';

import { isObject } from '@philips-emr/tws-utils';

export interface TypeChecker {
  (...args: any[]): any;
  __info__: object;
  addInfo: Function;
  readOnly: Function;
  notEnumerable: Function;
  group: Function;
  type: Function;
  placeholder: Function;
}

export interface PropertyInfo {
  name: string;
  enumerable: boolean;
  group: string;
  placeholder: string;
  readOnly: boolean;
  type: string;
}

type CreatorArgs = TypeChecker | TypeChecker[] | { [k: string]: TypeChecker };

/**
 * Checks whether or not the provided argument is a type checker
 *
 * @param {unknown} value the argument to be checked
 */
const isTypeChecker = (value: unknown): boolean => typeof value === 'function';

/**
 * Creates a copy of the provided function
 *
 * @param {Function} fn the function to be copied
 */
const copyFunction = (fn: Function): Function => fn.bind(null);

/**
 * Checks whether or not the received argument is an array or an object
 *
 * @param {unknown} param the value to be checked
 */
const isArrayOrObject = (param: unknown): boolean => (
  Array.isArray(param) || isObject(param)
);

/**
 * Discover the type checker name be it from React or custom
 *
 * @param {Function} typeChecker the type checker
 */
function getTypeCheckerName(typeChecker: Function): string {
  const ptEntries = Object.entries(PropTypes);

  const [typeCheckerName = ''] = ptEntries.find(([, value]) => {
    return value === typeChecker;
  }) || [];

  return typeCheckerName || 'custom';
}

/**
 * Adds utility methods to the type checker
 *
 * @param {Function} typeChecker the type checker
 */
function createChainableTypeChecker(typeChecker: Function): TypeChecker {
  const chainableTypeChecker = copyFunction(typeChecker) as TypeChecker;
  const addInfo = withInfo.bind(null, chainableTypeChecker);

  const getInfoStep = (name: string, defaultValue?: any): Function => (
    (value: any) => addInfo({ [name]: value || defaultValue })
  );

  chainableTypeChecker.addInfo = addInfo;
  chainableTypeChecker.readOnly = getInfoStep('readOnly', true);
  chainableTypeChecker.notEnumerable = getInfoStep('enumerable', false);
  chainableTypeChecker.group = getInfoStep('group');
  chainableTypeChecker.type = getInfoStep('type');
  chainableTypeChecker.placeholder = getInfoStep('placeholder');

  return chainableTypeChecker;
}

/**
 * Checks whether or not a custom type checker is enumerable
 *
 * @param {PropertyInfo} The custom type checker property information
 */
export function isEnumerable(prop: PropertyInfo): boolean {
  return prop.enumerable !== false;
}

/**
 * Adds information to a custom type checker,
 * merging the provided information with the existing one when necessary
 *
 * @param {TypeChecker} typeChecker the custom type checker
 * @param {object} info the data to be merged with the existing property information
 */
export function withInfo(typeChecker: Function, info?: object): TypeChecker {
  const oldInfo = extractInfo(typeChecker as TypeChecker);

  const tcWithInfo = createChainableTypeChecker(typeChecker);
  tcWithInfo.__info__ = { ...oldInfo, ...info };

  return tcWithInfo;
}

/**
 * Extracts the property information of a custom type checker
 *
 * @param {TypeChecker} typeChecker the custom type checker
 */
export function extractInfo(typeChecker: TypeChecker): object {
  const { __info__ } = typeChecker;

  return __info__ || ({ type: getTypeCheckerName(typeChecker) });
}

/**
 * Extracts the information of any type checker found within the iterable.
 *
 * @param {object} props an iterable containing the type checkers
 */
export function getPropsInfo(props: object): PropertyInfo[] {
  return Object.entries(props).map(([name, value]) => (
    isTypeChecker(value) ? ({ name, ...extractInfo(value) }) : value
  ));
}

/**
 * Processes the creator arguments, extracting information recusively if needed.
 *
 * @param {CreatorArgs} rawArgs the arguments provided to the creator
 * @param {boolean} recursive whether or not to extract information from the arguments recursively
 */
function processCreatorArgs(rawArgs: CreatorArgs, recursive: boolean = false): object | object[] | CreatorArgs {
  if (recursive) {
    if (isTypeChecker(rawArgs)) {
      return extractInfo(rawArgs as TypeChecker);
    } else if (isArrayOrObject(rawArgs)) {
      return getPropsInfo(rawArgs as Iterable<any>);
    }
  }

  return rawArgs;
}

/**
 * Wraps a creator type checker (Ex.: onOf, shape)
 * and captures the parameters storing it in the args property
 *
 * @param {Function} creator the creatir
 * @param {object} info the information to be merged with the property information
 * @param {boolean} recursive whether or not to extract information from the arguments recursively
 */
export function wrapCreator(creator: Function, info: object = {}, recursive: boolean = false): Function {
  return (rawArgs: CreatorArgs = [], ...rest: any[]) => {
    const args = processCreatorArgs(rawArgs, recursive);

    return withInfo(creator(rawArgs, ...rest), { ...info, args });
  };
}

export default {
  extractInfo,
  getPropsInfo,
  isEnumerable,
  withInfo,
  wrapCreator,
};
